import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/min/locales';
import { standardRequest, getErrorText } from 'shared/utils/request';
import { InformationIcon } from '@grnhse/seedling/lib/azalea/icons';
import { AvailabilityContainer } from './styles';
import delve from 'delve';
import {
  colors,
  fontSize,
  fontWeight,
  padding,
} from '@grnhse/seedling/lib/azalea/constants';
import Button from '@grnhse/seedling/lib/azalea/components/button';
import TextArea from 'shared/components/TextArea';
import SuggestedTimes from './suggested_times';
import CandidateTimes from './candidate_times';
import { assignLocation } from '../shared/utils/navigation';
import { getCurrentLocale } from 'shared/utils/localeUtils';
import { withLocalePrefix } from 'shared/utils/translation';
import { canAssumeTouchDevice } from 'shared/utils/touch_detection';
import { canAssumeMobileDevice, mergeAndSimplifyTimeSlots } from './utils';
import CandidatePronunciationRecorder from 'candidate_availability/candidate_pronunciation_recorder';
import CandidatePronounsField from 'candidate_availability/candidate_pronouns_field';
import {
  findPronunciationAudio,
  findPronounValues,
} from 'candidate_availability/form_utils';

const t = withLocalePrefix('availability.choose_availability');

const ScheduleBlockShape = {
  id: PropTypes.number, // some form of unique identifier for each suggested time
  start: PropTypes.string, // iso8601 datetime string
  end: PropTypes.string, // iso8601 datetime string
};

const MINIMUM_TIME_SLOT_MS = 1000 * 60 * 15; // 15 min

/**
 * NOTE: This doesn't listen for prop changes and update accordingly. Adding such functionality
 *       provided no benefit at the time of writing and has to account for moving times between
 *       suggested times -> user available times and such. Sorry bruh!
 * */
export default class TouchFriendlyCandidateAvailability extends React.Component {
  static propTypes = {
    suggested_times: PropTypes.arrayOf(PropTypes.shape(ScheduleBlockShape)),

    // specify this if the candidate is editing times he already submitted before
    candidate_availabilities: PropTypes.arrayOf(
      PropTypes.shape(ScheduleBlockShape)
    ),

    // specify this if the candidate is editing times he already submitted before
    // and has a note.
    note: PropTypes.string,

    // the path to submit scheduling info to
    submit_path: PropTypes.string.isRequired,

    // the path to go to after submission.
    confirmation_path: PropTypes.string.isRequired,

    // if set to true, it will filter out events that are in the past
    // this should really always be true but is here to make it easier to test
    excludePastTimes: PropTypes.bool,

    // if set to true, will make the name pronunciation recorder availability
    canUseCandidatePronunciation: PropTypes.bool,

    // optional recording file name
    candidatePronunciationRecordingName: PropTypes.string,

    // optional recording url string
    candidatePronunciationRecordingUrl: PropTypes.string,
  };

  static defaultProps = {
    excludePastTimes: true,
    suggested_times: [],
    candidate_availabilities: [],
    note: '',
  };

  constructor(props) {
    super(props);

    const {
      suggested_times,
      candidate_availabilities,
      note,
      excludePastTimes,
    } = this.props;

    const suggestedTimes =
      this.convertScheduleBlocksToMomentTimeRanges(suggested_times);

    let candidateAvailabilities = this.convertScheduleBlocksToMomentTimeRanges(
      candidate_availabilities
    );

    // for candidate availabilities, strip any past events out if flag is enabled
    if (excludePastTimes) {
      candidateAvailabilities = this.filterOutPastEvents(
        candidateAvailabilities
      );
    }

    this.state = {
      touchFriendly: false,
      submitting: false,
      error: null,

      note: note || '',
      suggestedTimes: suggestedTimes,
      candidateAvailabilities: candidateAvailabilities,
    };
  }

  convertScheduleBlockToMomentTimeRange(block) {
    return {
      id: block.id,
      start: moment(block.start),
      end: moment(block.end),
    };
  }

  convertScheduleBlocksToMomentTimeRanges(scheduleBlocks) {
    return scheduleBlocks.map((block) =>
      this.convertScheduleBlockToMomentTimeRange(block)
    );
  }

  filterOutPastEvents = (timeRanges) => {
    return timeRanges.filter(
      (timeRange) => !timeRange.start.isBefore(moment())
    );
  };

  canSubmit() {
    const { note, candidateAvailabilities } = this.state;

    return (
      candidateAvailabilities.length > 0 ||
      (typeof note === 'string' && note.length > 0)
    );
  }

  submitCandidateTimes = () => {
    const { submit_path, confirmation_path } = this.props;
    const { note, candidateAvailabilities, submitting } = this.state;

    if (!this.canSubmit()) {
      this.showErrorBanner(t('errors.no_availabilities_or_notes_entered'));
      return;
    }

    if (submitting) {
      return;
    }

    const data = {
      notes: note,
      events: {
        candidate: mergeAndSimplifyTimeSlots(candidateAvailabilities).map(
          (dateTimeRange) => {
            return {
              start: dateTimeRange.start.toISOString(),
              end: dateTimeRange.end.toISOString(),
            };
          }
        ),
      },
      candidate_pronunciation_data_url: findPronunciationAudio(),
      pronouns_attributes: findPronounValues(),
    };

    this.setState({ submitting: true, error: null });

    standardRequest
      .post(submit_path, data)
      .then(() => {
        assignLocation(confirmation_path);
        this.setState({ submitting: false });
      })
      .catch((res) => {
        const serverError = getErrorText(delve(res, 'response.data'));

        this.setState({
          submitting: false,
        });

        this.showErrorBanner(serverError);
      });
  };

  UNSAFE_componentWillMount() {
    // If we can't go off touch interface methods, check to see if this device is in
    // small screen territory
    const isTouchOrMobile = canAssumeTouchDevice() || canAssumeMobileDevice();
    const currLocale = getCurrentLocale();

    this.setState({
      touchFriendly: isTouchOrMobile,
      locale: currLocale,
    });

    // set the global locale used to be the detected locale
    moment.locale(currLocale);
  }

  /**
   * @returns a new set of suggestedTimes with the specified suggested time changed
   * */
  setAddedFlagForSuggestedTime(suggestedTimeRange, flagValue) {
    const { suggestedTimes } = this.state;
    const newSuggestedTimeRanges = [...suggestedTimes];

    for (let i = 0; i < newSuggestedTimeRanges.length; i++) {
      const currSuggestedTimeRange = newSuggestedTimeRanges[i];

      if (currSuggestedTimeRange.id === suggestedTimeRange.id) {
        newSuggestedTimeRanges[i] = Object.assign({}, suggestedTimeRange, {
          alreadyAdded: flagValue,
        });
        break;
      }
    }

    return newSuggestedTimeRanges;
  }

  haveTimeRangesChanged(prevTimeRanges, newTimeRanges) {
    if (prevTimeRanges.length !== newTimeRanges.length) {
      return true;
    }

    for (let i = 0; i < newTimeRanges.length; i++) {
      const prevTimeRange = prevTimeRanges[i];
      const newTimeRange = newTimeRanges[i];

      if (
        prevTimeRange.start !== newTimeRange.start ||
        prevTimeRange.end !== newTimeRange.end
      ) {
        return true;
      }
    }

    return false;
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!this.state.touchFriendly) {
      return false;
    }

    const {
      error: oldError,
      suggestedTimes: oldSuggestedTimes,
      submitting: prevSubmitting,
      candidateAvailabilities: oldCandidateAvailabilities,
      note: oldNote,
    } = this.state;

    const { error, suggestedTimes, submitting, candidateAvailabilities, note } =
      nextState;

    if (
      oldError !== error ||
      oldSuggestedTimes !== suggestedTimes ||
      prevSubmitting !== submitting ||
      note !== oldNote ||
      this.haveTimeRangesChanged(
        oldCandidateAvailabilities,
        candidateAvailabilities
      )
    ) {
      return true;
    }

    return false;
  }

  showErrorBanner = (msg) => {
    this.setState({ error: msg });

    // hide the error after 3 seconds
    window.setTimeout(() => {
      this.setState({ error: null });
    }, 3000);
  };

  onNoteChange = (evt) => {
    this.setState({ note: evt.target.value });
  };

  onCandidateTimesChanged = (timeRanges, removedTimeRange = null) => {
    const changes = { candidateAvailabilities: timeRanges };

    if (removedTimeRange) {
      changes.suggestedTimes = this.setAddedFlagForSuggestedTime(
        removedTimeRange,
        false
      );
    }

    this.setState(changes);
  };

  addSuggestedTimeAsAvailability = (suggestedTimeRange) => {
    const { candidateAvailabilities } = this.state;

    const newSuggestedTimes = this.setAddedFlagForSuggestedTime(
      suggestedTimeRange,
      true
    );

    // since suggested times have start/end values as ISO8601 strings instead of moment
    // objects, "fix that" by doing the conversion
    //
    // once done, attach a prop called 'originalSuggestedTime' used solely for keeping track
    // of the original suggested time used to create this candidate time. If this is removed,
    // the reference is used to show the suggested time back in the "Suggested times" list
    const newCandidateTimeRange =
      this.convertScheduleBlockToMomentTimeRange(suggestedTimeRange);
    newCandidateTimeRange.originalSuggestedTime = suggestedTimeRange;

    this.setState({
      suggestedTimes: newSuggestedTimes,
      candidateAvailabilities: candidateAvailabilities.concat([
        newCandidateTimeRange,
      ]),
    });
  };

  render() {
    const {
      canUseCandidatePronunciation,
      candidatePronunciationRecordingName,
      candidatePronunciationRecordingUrl,
      canUseCandidatePronouns,
      candidatePronouns,
      candidateDefaultPronouns,
    } = this.props;

    const {
      touchFriendly,
      suggestedTimes,
      submitting,
      candidateAvailabilities,
      note,
      error,
    } = this.state;

    const hasValidData = this.canSubmit();

    if (!touchFriendly) {
      return null;
    }

    return (
      <Container id="touch_screen_availability_widget">
        {error && <Error>{error}</Error>}

        <Notice>
          <InformationIcon width={20} height={20} />
          <span>{t('suggested_times.caption')}</span>
        </Notice>

        {suggestedTimes.length > 0 && (
          <section className="suggested-times">
            <SectionHeader>{t('suggested_times.header')}</SectionHeader>
            <SuggestedTimes
              suggestedTimes={suggestedTimes}
              addSuggestedTimeAsAvailability={
                this.addSuggestedTimeAsAvailability
              }
            />
          </section>
        )}

        <section className="candidate-times">
          <SectionHeader>{t('my_availability')}</SectionHeader>
          <CandidateTimes
            candidateTimes={candidateAvailabilities}
            step={MINIMUM_TIME_SLOT_MS}
            onChange={this.onCandidateTimesChanged}
          />
        </section>

        <section>
          <SectionHeader>{t('notes.header')}</SectionHeader>
          <TextArea
            className="mobile-input-text"
            placeholder={t('notes.placeholder')}
            onChange={this.onNoteChange}
            value={note}
            block
          />
        </section>

        {canUseCandidatePronunciation && (
          <CandidatePronunciationRecorder
            recordingName={candidatePronunciationRecordingName}
            recordingUrl={candidatePronunciationRecordingUrl}
          />
        )}

        {canUseCandidatePronouns && (
          <CandidatePronounsField
            pronouns={candidatePronouns}
            defaultPronounsOptions={candidateDefaultPronouns}
            isMobile={true}
          />
        )}

        <SubmitButton
          size="large"
          onClick={this.submitCandidateTimes}
          disabled={submitting || !hasValidData}
          fancy
          block
        >
          {submitting ? t('actions.submitting') : t('actions.submit')}
        </SubmitButton>
      </Container>
    );
  }
}

const Container = styled.div`
  padding: ${padding.normal} 0;

  textarea {
    // the default browser font for this is 'monospace' on mobile devices
    // reset this to inherit from our styles. We aren't trying to make their UI
    // look like dev terminals.
    font-family: inherit;
  }

  select {
    ${fontWeight('heavy')};
  }

  section + section {
    margin-top: ${padding.normal};
  }

  .invalid {
    border: 1px solid ${colors.darkRed};
  }

  .mobile-input-text,
  select {
    // this is important. It needs to match the browser default font size
    // or otherwise, on mobile devices, it will auto zoom into the field even if the page
    // is mobile-optimized
    @media (max-width: 320px) {
      // For small screen sizes, 16px font-size makes the text too big to fit
      // so reduce this to a little less than default font size
      font-size: 0.9rem;
    }

    @media (min-width: 321px) {
      font-size: 16px !important;
    }
  }

  .suggested-times {
    // to add a nice divider between available times and suggested times
    border-bottom: 1px solid ${colors.lightGrey};
    padding-bottom: ${padding.normal};
    margin-bottom: ${padding.normal};
  }
`;

export const Error = styled.div`
  // have this show up on top of the screen
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  background: ${colors.darkRed};
  color: ${colors.white};
  ${fontWeight('heavy')};

  padding: ${padding.small};

  z-index: 9999;
`;

const SectionHeader = styled.div`
  color: ${colors.mediumGrey};
  font-size: ${fontSize.large};
  ${fontWeight('heavy')};

  margin: 3vh 0;
`;

const Notice = styled.legend`
  position: relative;
  border: 1px solid ${colors.lightGrey};
  padding: 15px;
  ${fontWeight('heavy')};

  svg {
    position: absolute;
    left: 0;

    // this is eyeballed to appear vertically aligned with the text by taking the padding
    // of the container and subtracting a few pixels to account for the height.
    top: calc(15px - 2px);

    margin-left: 15px; // to offset a little more from the left edge
  }

  span {
    padding-left: calc(15px * 2); // to offset it from the icon
    display: block;
  }
`;

export const SuggestedTimeAvailabilityContainer = styled(AvailabilityContainer)`
  .suggested-time {
    display: inline-block;
    vertical-align: middle;
    margin-right: ${padding.small};
    width: calc(100% - 100px - ${padding.small});
  }

  .add-suggested-time {
    display: inline-block;
    vertical-align: middle;
    width: 100px;
  }
`;

export const SubmitButton = styled(Button)`
  margin-top: ${padding.large};
`;
